import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FAQ.css';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How much does rooftop solar cost? What about government subsidy?',
      answer: 'Solar installation costs depend on factors such as panel choice, system size, and inverter type. The government offers substantial subsidies through the PM Surya Ghar scheme: ₹30,000 for 1kW, ₹60,000 for 2kW, and ₹78,000 for 3kW or above. The Ministry of New and Renewable Energy (MNRE) transfers the subsidy directly to your bank account within 30 days after local discom inspection.'
    },
    {
      question: 'What about easy financing? Can I get a loan to go solar?',
      answer: 'Yes! We offer EMI plans with just ₹1 downpayment. You can finance your solar system over 5 years with an attractive interest rate of 6% per annum. The best part? Your EMI will be less than your current electricity bill, so you start saving from day one! We handle all the paperwork and make the process seamless.'
    },
    {
      question: 'How much can I actually save by going solar?',
      answer: 'You can save over 90% on your electricity bills every month! For example, if your monthly bill is ₹3,000, you\'ll pay around ₹2,750 as EMI (which is less than your current bill). After 5 years, you own the system completely and enjoy free electricity for the next 20-25 years. Over 25 years, you can save between ₹13-14 lakh compared to paying electricity bills.'
    },
    {
      question: 'Do I need a battery with my solar system?',
      answer: 'For on-grid systems, you don\'t need a battery. During the day, excess electricity is sent to the grid, and at night, you draw from the grid. Your bill is calculated based on net usage. Batteries are only needed for off-grid systems, which are ideal for remote areas without grid connectivity.'
    },
    {
      question: 'How does rooftop solar work?',
      answer: 'Rooftop solar panels convert sunlight into electricity using photovoltaic cells. During the day, panels generate DC electricity, which is converted to AC by an inverter for home use. In an on-grid system, excess power goes to the grid, and you draw from the grid at night. Net metering ensures you only pay for the net electricity consumed.'
    },
    {
      question: 'Does solar work in all Indian states and across all seasons?',
      answer: 'Yes! Solar energy is viable in all parts of India throughout the year. While there are seasonal variations (higher generation in summer, lower during monsoon), solar systems are designed to meet annual energy needs. India receives abundant sunlight with approximately 300 clear sunny days per year, making it ideal for solar power.'
    },
    {
      question: 'How do I decide what size solar system will suit my needs?',
      answer: 'The system size depends on your monthly electricity consumption. Generally, 1kW generates about 1,400 units per year. Calculate your yearly consumption and divide by 1,400 to get the required KW. For example, if you consume 4,200 units per year, you need a 3kW system. Our questionnaire will help you determine the perfect size for your needs.'
    },
    {
      question: 'What type of solar system is best suited for my home?',
      answer: 'For most homes, an on-grid solar system is the best choice. It\'s more cost-effective, allows you to sell excess electricity back to the grid, and doesn\'t require battery maintenance. Off-grid systems are better for remote areas without grid connectivity. Our experts can help you choose based on your location and requirements.'
    },
    {
      question: 'Are solar systems difficult to maintain? Will I have to clean them frequently?',
      answer: 'Solar systems require minimal maintenance. Panels should be cleaned quarterly to maintain efficiency, especially in dusty areas. Most reputable solar providers, including Adityam Urja, offer maintenance packages. The system has no moving parts, so it\'s very reliable and can last 25-30 years with minimal upkeep.'
    },
    {
      question: 'Will I need to invest a lot of time and effort in buying solar?',
      answer: 'Not at all! With Adityam Urja, we handle everything for you - consultation, design, installation, paperwork (including subsidy application), and maintenance. Our 1 rupee downpayment and easy EMI process make it hassle-free. You just need to provide basic information, and we take care of the rest!'
    },
    {
      question: 'What are the eligibility criteria for government subsidy?',
      answer: 'To avail the subsidy: 1) Solar panels must be made in India, 2) Panels must comply with ALMM (Approved List of Models and Manufacturers) specification, 3) The solar system should be a rooftop on-grid solar system. We ensure all our installations meet these criteria and handle the subsidy application process for you.'
    },
    {
      question: 'How long does it take to recover the solar installation cost?',
      answer: 'The payback period is typically 4-5 years. After that, you enjoy free electricity for the next 20-21 years, giving you a net return of 21-30% on your investment. This is much higher than traditional investments like fixed deposits, mutual funds, or gold.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section">
      <div className="container">
        <motion.div
          className="faq-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about going solar</p>
        </motion.div>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span className="faq-question-text">{faq.question}</span>
                <motion.span
                  className="faq-icon"
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  ▼
                </motion.span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;

